import React from 'react';
import { motion } from 'motion/react';

import {
  Headphones,
  Users,
  Clock,
  Globe,
  Shield,
  TrendingUp,
  MessageSquare,
  PhoneCall,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Settings,
  Target,
  Award,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SpamProtectedForm from '../../components/SpamProtectedForm';
import '../../styles/services.css';

const features = [
  { icon: PhoneCall, title: 'Customer Support', desc: '24/7 multi-channel customer service solutions' },
  { icon: MessageSquare, title: 'Technical Support', desc: 'Expert technical assistance for your products' },
  { icon: BarChart3, title: 'Back Office', desc: 'Data entry, processing, and administrative tasks' },
  { icon: Target, title: 'Sales Support', desc: 'Lead generation and sales process optimization' },
  { icon: Users, title: 'HR Services', desc: 'Recruitment, payroll, and employee management' },
  { icon: Globe, title: 'Multilingual Support', desc: 'Support in multiple languages for global reach' },
];

const benefits = [
  'Reduce operational costs by up to 60%',
  'Access to skilled professionals 24/7',
  'Scale your team up or down as needed',
  'Focus on core business activities',
  'Improve customer satisfaction scores',
  'Enterprise-grade security and compliance',
];

const BPOServicesPage = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="service-hero bpo">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="service-hero-content"
          >
            <span className="service-badge">
              <Headphones size={16} /> Business Process Outsourcing
            </span>
            <h1>Scale Your Operations Efficiently</h1>
            <p>Professional BPO services that help you reduce costs, improve efficiency, and focus on what matters most - growing your business.</p>
            <div className="hero-buttons">
              <a href="#contact" className="btn-service-primary">
                Get Started <ArrowRight size={18} />
              </a>
              <a href="#features" className="btn-service-secondary">
                Our Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="service-features" id="features">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">Our Services</span>
            <h2>Comprehensive BPO Solutions</h2>
            <p>End-to-end business process outsourcing for enterprises of all sizes</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="feature-card"
              >
                <div className="feature-icon purple">
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="service-benefits">
        <div className="container-custom">
          <div className="benefits-wrapper">
            <div className="benefits-content">
              <span className="section-tag">Why Choose Us</span>
              <h2>Benefits of Our BPO Services</h2>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle size={20} /> {benefit}
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="benefits-stats">
              <div className="stat-card">
                <span className="stat-value">60%</span>
                <span className="stat-label">Cost Reduction</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">24/7</span>
                <span className="stat-label">Availability</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">99%</span>
                <span className="stat-label">Client Satisfaction</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">10+</span>
                <span className="stat-label">Languages</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="service-contact" id="contact">
        <div className="container-custom">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2>Let's Discuss Your Needs</h2>
              <p>Get a customized BPO solution tailored to your business requirements.</p>
              <ul className="contact-benefits">
                <li><CheckCircle size={20} /> Free needs assessment</li>
                <li><CheckCircle size={20} /> Custom pricing plans</li>
                <li><CheckCircle size={20} /> Dedicated account manager</li>
                <li><CheckCircle size={20} /> Quick implementation</li>
              </ul>
            </div>
            
            <SpamProtectedForm 
              emailSubject="BPO Services Inquiry"
              submitText="Send Message"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BPOServicesPage;
